import { MarketingCampaignTypes } from "@hightouch/lib/llm/lifecycle-studio/campaigns/types";

import {
  type AdStudioProjectNamesQuery,
  type AnalyticsExperimentsQuery,
  type DashboardsQuery,
  type MarketingCampaignsListQuery,
  type ReportSchedulesByIdsQuery,
  type SavedAnalyticsChartsQuery,
  useAdStudioProjectNamesQuery,
  useAnalyticsExperimentsQuery,
  useDashboardsQuery,
  useMarketingCampaignsListQuery,
  useRecentlyViewedQuery,
  useReportSchedulesByIdsQuery,
  useSavedAnalyticsChartsQuery,
} from "src/graphql";
import type { Meta } from "src/pages/home/route-utils";

// The rail only tracks the new project-based campaigns.
const CAMPAIGN_TYPE = MarketingCampaignTypes.Project;

const idsForResourceType = (
  metas: Meta[],
  resource: Meta["resource"],
): string[] =>
  metas.filter((meta) => meta.resource === resource).map((meta) => meta.id);

const selectCharts = (data: SavedAnalyticsChartsQuery) =>
  data.saved_analytics_charts;
const selectDashboards = (data: DashboardsQuery) => data.dashboards;
const selectExperiments = (data: AnalyticsExperimentsQuery) => data.experiments;
const selectCampaigns = (data: MarketingCampaignsListQuery) =>
  data.marketing_campaigns;
const selectProjects = (data: AdStudioProjectNamesQuery) =>
  data.ad_studio_projects;
const selectReports = (data: ReportSchedulesByIdsQuery) => data.report_schedule;

export type ResolveName = (meta: Meta) => string | null;

// Resolves display names for each recently-viewed resource type via its
// existing list query. Every query is gated so only resource types actually
// present in `metas` are fetched. Because `metas` is the deterministic ranked
// top set (see `rankRecentlyViewed`), both mounts of the list issue identical
// query variables and React Query dedupes them to a single fetch per type.
export const useRecentlyViewedNames = (
  metas: Meta[],
): {
  resolveName: ResolveName;
  isLoading: boolean;
  isError: boolean;
  refetchFailed: () => void;
} => {
  const audienceIds = idsForResourceType(metas, "audiences");
  const journeyIds = idsForResourceType(metas, "journeys");
  const syncIds = idsForResourceType(metas, "syncs");
  const modelIds = idsForResourceType(metas, "models");
  const chartIds = idsForResourceType(metas, "charts");
  const dashboardIds = idsForResourceType(metas, "dashboards");
  const experimentIds = idsForResourceType(metas, "experiments");
  const campaignIds = idsForResourceType(metas, "campaigns");
  const projectIds = idsForResourceType(metas, "ad-studio-projects");
  const reportIds = idsForResourceType(metas, "reports");

  const core = useRecentlyViewedQuery(
    { audienceIds, journeyIds, syncIds, modelIds },
    {
      enabled:
        audienceIds.length +
          journeyIds.length +
          syncIds.length +
          modelIds.length >
        0,
    },
  );
  const charts = useSavedAnalyticsChartsQuery(
    { filters: { id: { _in: chartIds } }, limit: chartIds.length || 1 },
    { enabled: chartIds.length > 0, select: selectCharts },
  );
  const dashboards = useDashboardsQuery(
    { filters: { id: { _in: dashboardIds } }, limit: dashboardIds.length || 1 },
    { enabled: dashboardIds.length > 0, select: selectDashboards },
  );
  const experiments = useAnalyticsExperimentsQuery(
    { filters: { id: { _in: experimentIds } } },
    { enabled: experimentIds.length > 0, select: selectExperiments },
  );
  const campaigns = useMarketingCampaignsListQuery(
    {
      type: CAMPAIGN_TYPE,
      limit: campaignIds.length || 1,
      offset: 0,
      searchFilter: { id: { _in: campaignIds } },
    },
    { enabled: campaignIds.length > 0, select: selectCampaigns },
  );
  const projects = useAdStudioProjectNamesQuery(
    { projectIds },
    { enabled: projectIds.length > 0, select: selectProjects },
  );
  const reports = useReportSchedulesByIdsQuery(
    { reportIds },
    { enabled: reportIds.length > 0, select: selectReports },
  );

  const queries = [
    core,
    charts,
    dashboards,
    experiments,
    campaigns,
    projects,
    reports,
  ];
  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const refetchFailed = () => {
    for (const query of queries) {
      if (query.isError) {
        void query.refetch();
      }
    }
  };

  const resolveName: ResolveName = (meta) => {
    switch (meta.resource) {
      case "audiences":
        return (
          core.data?.audiences.find((a) => String(a.id) === meta.id)?.name ??
          null
        );
      case "journeys":
        return (
          core.data?.journeys.find((j) => String(j.id) === meta.id)?.name ??
          null
        );
      case "models":
        return (
          core.data?.models.find((m) => String(m.id) === meta.id)?.name ?? null
        );
      case "syncs":
        return resolveSyncName(
          // 'satisfies' checks the query rows are assignable to MinimalSyncView.
          // Because its fields are optional, this catches a shape mismatch on
          // fields that remain, not the removal of an optional one.
          core.data?.syncs satisfies MinimalSyncView[] | undefined,
          meta.id,
        );
      case "charts":
        return charts.data?.find((c) => String(c.id) === meta.id)?.name ?? null;
      case "dashboards":
        return (
          dashboards.data?.find((d) => String(d.id) === meta.id)?.name ?? null
        );
      case "experiments":
        return (
          experiments.data?.find((e) => String(e.id) === meta.id)?.name ?? null
        );
      case "campaigns":
        return (
          campaigns.data?.find((c) => String(c.id) === meta.id)?.name ?? null
        );
      case "ad-studio-projects":
        return (
          projects.data?.find((p) => String(p.id) === meta.id)?.name ?? null
        );
      case "reports":
        return (
          reports.data?.find((r) => String(r.id) === meta.id)?.name ?? null
        );
    }
  };

  return { resolveName, isLoading, isError, refetchFailed };
};

// Minimal view of a `RecentlyViewedQuery` sync row — only the fields name
// resolution reads — kept small so test fixtures stay tiny.
export type MinimalSyncView = {
  id: string | number;
  segment?: { name: string } | null;
  destination?: {
    name?: string | null;
    definition?: { name: string } | null;
  } | null;
};

// A sync has no name of its own; show "model → destination" (falling back to
// whichever side resolves) so the row reads like the sync list.
export const joinSyncName = (
  modelName: string | null | undefined,
  destinationName: string | null | undefined,
): string | null =>
  modelName && destinationName
    ? `${modelName} → ${destinationName}`
    : (modelName ?? destinationName ?? null);

export const resolveSyncName = (
  syncs: readonly MinimalSyncView[] | undefined,
  id: string,
): string | null => {
  const sync = syncs?.find((sync) => String(sync.id) === id);
  if (!sync) return null;
  return joinSyncName(
    sync.segment?.name,
    sync.destination?.name ?? sync.destination?.definition?.name,
  );
};
